import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, ElementRef, HostBinding, HostListener, OnInit, signal, inject, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import {
  ApplicationService,
  UserService,
  IUser,
  UserRoleEnum,
  IUserDetails,
  ApplicationTypeEnum,
  slideFromLeft,
  ThemeService,
  AgentParam,
  ScheduleTypeEnum,
  CLAIM_OPEN_ID,
  MediaRulesService,
  AuviousRtcService,
  GenericErrorHandler,
  ConversationOriginEnum,
  KeyboardService,
  KeyCodes,
  ModalService,
  IApplication,
  IApplicationConfig,
} from "../../../core-ui";
import { AuthState } from "../../../core-ui/models/AuthState";
import { AppConfigService } from "../../../core-ui/services/app.config.service";
import { IntegrationService, MenuService } from "../../services";
import { AUDIT_ROLES, REPORTS_ROLES, SETTINGS_ROLES } from "../../app.enums";

@Component({
  selector: "app-user-menu",
  templateUrl: "./user-menu.component.html",
  styleUrls: ["./user-menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideFromLeft],
  standalone: false,
})
export class UserMenuComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private applicationService = inject(ApplicationService);
  private userService = inject(UserService);
  private themeService = inject(ThemeService);
  private cd = inject(ChangeDetectorRef);
  private config = inject(AppConfigService);
  private menu = inject(MenuService);
  private mediaRules = inject(MediaRulesService);
  private rtcService = inject(AuviousRtcService);
  private errorHandler = inject(GenericErrorHandler);
  private integration = inject(IntegrationService);
  private keyboard = inject(KeyboardService);
  private modalService = inject(ModalService);

  open = signal(false);
  user = signal<IUser>(undefined);
  details = signal<IUserDetails>(undefined);
  isInPageWithoutMenu = signal(true);
  idOn = signal(false);
  avatarError = signal(false);
  isHelpOn = signal(true);
  toggleColorMode = signal<"light" | "dark">("dark");
  application = signal<IApplication>(undefined);
  applicationConfig = signal<IApplicationConfig>(undefined);

  isAgent = signal(false);
  isCustomer = signal(false);
  isSupervisor = signal(false);
  isAdmin = signal(false);
  isConnectionDown = signal(false);
  isActivityEnabled = signal(false);
  isModalOpen = signal(false);

  id = computed(() => this.user()?.getClaim(CLAIM_OPEN_ID));
  roles = computed(() => this.user()?.getRoles());
  displayName = computed(() => this.details()?.name);
  avatar = computed(() => this.details()?.avatarUrl);

  isConfigEnabled = computed(() => this.hasAnyRole(SETTINGS_ROLES));
  isInteractionsEnabled = computed(() => this.isSupervisor() || this.isAgent());
  isAuditEnabled = computed(() => this.hasAnyRole(AUDIT_ROLES));
  isMetricsEnabled = computed(() => this.hasAnyRole(REPORTS_ROLES));
  isToggleDisabled = computed(() => this.isConnectionDown());
  isRecordingEnabled = computed(
    () =>
      this.isSupervisor() ||
      (this.isAgent() && this.mediaRules.canExportRecording)
  );
  isScheduleEnabled = computed(
    () =>
      (this.isAgent() || this.isAdmin() || this.isSupervisor()) &&
      this.integration.supportsInteractionOrigin(
        ConversationOriginEnum.APPOINTMENT
      ) &&
      this.applicationConfig()?.agentParameters[AgentParam.SCHEDULE_ENABLED] &&
      this.applicationConfig()?.agentParameters[AgentParam.SCHEDULE_CHANNEL] ==
        ScheduleTypeEnum.appointment
  );
  isApplicationConfigured = computed(() => !!this.application()?.getId());

  // isVisible = computed(
  //   () =>
  //     !!this.user() &&
  //     !this.isModalOpen() &&
  //     !this.isCustomer() &&
  //     ((this.isAgent() &&
  //       (!this.isSupervisor() || !this.isAdmin()) &&
  //       this.config.agentParamEnabled(AgentParam.MAIN_MENU_ENABLED)) ||
  //       this.isSupervisor() ||
  //       this.isAdmin()) &&
  //     !this.isInPageWithoutMenu() &&
  //     this.isApplicationConfigured &&
  //     (this.isHelpOn() ||
  //       this.isRecordingEnabled() ||
  //       this.isInteractionsEnabled() ||
  //       this.isConfigEnabled() ||
  //       this.isMetricsEnabled())
  // );

  isVisible = computed(() => {
    // Basic requirements
    if (
      !this.user() ||
      this.isModalOpen() ||
      this.isCustomer() ||
      this.isInPageWithoutMenu() ||
      !this.isApplicationConfigured()
    ) {
      return false;
    }

    // User role check
    const hasValidRole =
      this.isAdmin() ||
      this.isSupervisor() ||
      (this.isAgent() &&
        this.applicationConfig()?.agentParameters[
          AgentParam.MAIN_MENU_ENABLED
        ]);

    if (!hasValidRole) {
      return false;
    }

    // Feature availability check
    const hasFeatures =
      this.isHelpOn() ||
      this.isRecordingEnabled() ||
      this.isInteractionsEnabled() ||
      this.isConfigEnabled() ||
      this.isMetricsEnabled();

    return hasFeatures;
  });

  readonly menuRef = viewChild("menu", { read: ElementRef });

  constructor() {
    const router = this.router;
    const route = this.route;

    this.userService.userChanged$
      .pipe(filter((data) => !!data?.user))
      .subscribe(({ user, details }) => {
        this.user.set(user);
        this.details.set(details);
        if (user) {
          this.isAgent.set(user.hasRole(UserRoleEnum.agent));
          this.isCustomer.set(user.hasRole(UserRoleEnum.customer));
          this.isSupervisor.set(user.hasRole(UserRoleEnum.supervisor));
          this.isAdmin.set(user.hasRole(UserRoleEnum.admin));
          this.isActivityEnabled.set(this.isSupervisor());
        }
        /*
        this.isHelpOn = !(this.applicationService.getActiveApplication().getType() === ApplicationTypeEnum.Genesys && this.device.isIFrame);
        */
        // this.cd.detectChanges();
      });

    router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        const path = route.children?.[0]?.snapshot.url?.[0].path;
        // hide menu in case we are in a call
        this.isInPageWithoutMenu.set(
          ["a", "t", "thank-you", "error", "cobrowse"].includes(path)
        );
        // this.cd.detectChanges();
      });

    effect(() => {
      if (this.open()) {
        requestAnimationFrame(this.onHomeKey);

        this.keyboard.listen(KeyCodes.Esc, this.onEscKey);
        this.keyboard.listen(KeyCodes.Home, this.onHomeKey);
        this.keyboard.listen(KeyCodes.End, this.onEndKey);
      } else {
        this.keyboard.unlisten(KeyCodes.Esc, this.onEscKey);
        this.keyboard.unlisten(KeyCodes.Home, this.onHomeKey);
        this.keyboard.unlisten(KeyCodes.End, this.onEndKey);
      }
    });
  }

  @HostBinding("class") get class() {
    return {
      open: this.open(),
      "talkdesk-menu": this.applicationService.isTalkdeskApp,
    };
  }

  @HostListener("click")
  click() {
    if (this.open()) {
      this.open.set(false);
    }
  }

  ngOnInit(): void {
    this.errorHandler.connectionError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
        this.isConnectionDown.set(true);
      });
    this.rtcService.connectionRegistered$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
        this.isConnectionDown.set(false);
      });
    this.themeService.themeChanged$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((theme) => {
        this.toggleColorMode.set(theme?.darkMode ? "light" : "dark");
      });
    this.modalService.visibilityChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((open) =>
        this.isModalOpen.set(open)
      );
    this.applicationService.applicationChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((a) =>
        this.application.set(a)
      );
    this.config.configChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((c) => this.applicationConfig.set(c));
  }

  private hasAnyRole(allowed: UserRoleEnum[]): boolean {
    const roles = this.roles() ?? [];
    return allowed.some((role) => roles.includes(role));
  }

  active(path) {
    return path === this.route.snapshot.children?.[0]?.url?.[0].path;
  }

  private onEscKey = async () => {
    await this.toggle();
    this.cd.detectChanges();
  };

  private onHomeKey = () => {
    (
      this.menuRef().nativeElement?.querySelector(
        "[tabindex]:first-child"
      ) as HTMLElement
    ).focus();
  };

  private onEndKey = () => {
    (
      this.menuRef().nativeElement?.querySelector(
        "[tabindex]:last-child"
      ) as HTMLElement
    ).focus();
  };

  async toggle(e?: Event) {
    e?.stopPropagation();
    this.open.set(!this.open());
  }

  async go(toPath) {
    try {
      await this.menu.checkForUnsavedChanges();

      // check if we have any leftover conversation ids in state (in case we came from a redirect in premise)
      const authState = AuthState.fromSerializedState(
        this.route.snapshot.queryParamMap.get("state")
      );

      const fromPath = this.route.children?.[0]?.snapshot.url?.[0].path;

      let queryParams = this.route.snapshot.queryParams;

      if (fromPath === "recordings" && !!authState?.conversationId) {
        // clear id
        authState.setConversationId(undefined);
        queryParams = {
          ...this.route.snapshot.queryParams,
          state: authState.serialize(),
        };
      } else if (fromPath === "interaction") {
        queryParams = {
          ...this.route.snapshot.queryParams,
          startAt: null,
          endAt: null,
          page: null,
        };
      }
      this.router.navigate(["/", toPath], {
        queryParams,
        queryParamsHandling: "merge",
      });
      this.open.set(false);
    } catch (ex) {
      // decided to stay in page due to unsaved changes
      this.open.set(false);
      this.cd.detectChanges();
    }
  }

  help() {
    window.open("https://docs.auvious.video", "_blank");
  }

  cancel(e: Event) {
    e.stopPropagation();
  }

  toggleId(e: Event) {
    e.stopPropagation();
    if (this.application().getType() === ApplicationTypeEnum.GenesysCloud) {
      return;
    }
    this.idOn.set(!this.idOn());
  }
}
